import {
  Component,
  OnInit,
  Inject,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormArray,
  UntypedFormControl,
  AbstractControl,
} from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  MatLegacyTabChangeEvent as MatTabChangeEvent,
  MatLegacyTabGroup as MatTabGroup,
} from '@angular/material/legacy-tabs';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

// Configs
import { SnackBarConfig } from '../../../shared/configs';

// Interfaces
import { OptionInterface } from '../../../shared/interfaces/common.interface';

// Services
import { FactoryApiService } from '../../../shared/services/api/factory-api.service';
import { RfqApiService } from '../../../shared/services/api/rfq/rfq-api.service';
import { ShippingCarrierApiService } from '../../../shared/services/api/shipping-carrier-api.service';
import { SystemSettingApiService } from '../../../shared/services/api/system-setting-api.service';
import { UnitClassApiService } from '../../../shared/services/api/unit-class-api.service';
import { IUnitClass, IUnitOfMeasure } from '../../../unit-of-measure';

import { UnitOfMeasureApiService } from './../../../shared/services/api/unit-of-measure-api.service';
import { SystemSettingInterface } from './../../../system-setting/interfaces';
import { DepartmentConfig } from './../../config';
import { ERfqTabs, ICategory, EProductTypes, IFactory } from './../../interfaces';

@Component({
  selector: 'lum-rfq-form',
  templateUrl: './rfq-form.component.html',
  styleUrls: ['./rfq-form.component.scss'],
})
export class RfqFormComponent implements OnInit {
  @ViewChild('customFieldContainerRef', { read: ViewContainerRef })
  customFieldContainerRef :ViewContainerRef;

  form :UntypedFormGroup;
  tab :string = 'Product Info';
  tabs :string[] = [
    'Product Information',
    'Kit Items',
    'Attachments',
    'Shipping and Packing Details',
    'Others',
  ];
  tabEnums = ERfqTabs;
  categories :ICategory[] = [];
  productTypes :string[] = [EProductTypes.PRODUCT, EProductTypes.KIT];
  uploadedFilesCount :number = 0;
  uploadErrors :any = [];
  uploadInitiated :boolean = false;

  categories$ :Observable<ICategory[]>;
  factories$ :Observable<IFactory[]>;
  productTags$ :Observable<OptionInterface[]>;
  baseUnitClass$ :Observable<IUnitClass>;
  unitClasses$ :Observable<IUnitClass[]>;
  unitMeasures$ :Observable<IUnitOfMeasure[]>;
  defaultWarehouseLocations$ :Observable<any>;
  settings$ :Observable<SystemSettingInterface[]>;
  currentRfq$ :Observable<any>;
  submittingForm$ :Observable<boolean>;

  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data :{
      parentSku :any;
      rfq :any;
      featureFlag :any;
      permissions :any;
      initialData :any;
    },
    private dialog :MatDialogRef<RfqFormComponent>,
    private factoryApiService :FactoryApiService,
    private fb :UntypedFormBuilder,
    private rfqApiService :RfqApiService,
    private settingsApiService :SystemSettingApiService,
    private shippingCarrierApiService :ShippingCarrierApiService,
    private snackBar :MatSnackBar,
    private unitClassService :UnitClassApiService,
    private unitOfMeasureApiService :UnitOfMeasureApiService,
  ) {}

  ngOnInit() :void {
    this.getDataFromService();
    this.initForm();
    this.initActions();
    this.initSubscriptions();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  selectedTab(evt :MatTabChangeEvent) :void {
    this.tab = this.tabs[evt.index];
  }

  onClose() :void {
    this.dialog.close();
  }

  onSubmit() :void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackBar.open('Please fill the required fields.', 'Error', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    if (this.isKit && !this.hasKitItems) {
      this.snackBar.open('Please select at least 1 kit item.', 'Error', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    const formattedData = this.getFormattedData();
    this.rfqApiService
      .create(formattedData)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :{ files :any; message :string; record :any }) => {
          if (this.fileRemarksControl.value.length > 0) {
            this.uploadInitiated = true;
            this.uploadFiles(response.record);
          } else {
            this.snackBar.open('Successfully created client SKU.', 'Success', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.onClose();
          }
        },
        error: (err) => {
          if (err.error_type && err.error_type === 'Existing') {
            this.snackBar.open(err.message, 'Error', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          } else {
            this.snackBar.open(
              'Error saving record. Please try again later or contact support.',
              'Error',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
          }
        },
      });
  }

  onCustomFieldChange(customField :string) :void {
    this.form.get('customFieldValues')?.patchValue(customField);
  }

  private initForm() :void {
    const initialData = this.data?.initialData;

    this.form = this.fb.group({
      product_name: [initialData?.product_name || '', Validators.required],
      product_type_id: [2, Validators.required],

      product_dept_id: [null],
      is_palletized: [null],
      cartons_per_pallet: [null],
      shipment_value: [null],
      units_per_carton: [null],
      units_per_pallet: [null],

      pallet_length: [null],
      pallet_width: [null],
      pallet_height: [null],

      shipping_length: [null],
      shipping_width: [null],
      shipping_height: [null],

      product_length: [null],
      product_width: [null],
      product_height: [null],

      box_length: [null],
      box_width: [null],
      box_height: [null],
      carton_length: [null],
      carton_width: [null],
      carton_height: [null],
      quote_expires: [false],
      expires_at: [''],

      sub_category_id: [0],
      product_category_id: ['', [Validators.required, Validators.min(1)]],
      product_description: [''],
      units_qty_annual: [0],
      units_qty_first: [0],

      reorder_level: [0],
      qty_limit_notify: [0],
      oss_limit_notify: [0],

      volume_first_order: [1],
      volume_annual_order: [1],
      target_price: [1],

      will_purchase_sample: [''],
      is_product_in_market: [''],
      internal_sku: [initialData?.internal_sku || '', Validators.required],
      upc: [''],

      rfq_quote_type: [16, Validators.required],

      units_qty_annual_type: [''],
      address_not_known: [false],
      changes_to_make: [''],
      development_status: [''],
      units_qty_moq: [''],
      brand_product: [''],
      artwork_available: [''],
      product_has_variants: [''],

      pr_labeling_stickers: [''],
      pr_custom_printing: [''],
      pr_bundling: [''],
      pr_various_prep: [''],
      pr_carton_arrangement: [''],
      pr_same_as_link: [''],
      pr_generic_quote: [''],

      pr_labeling_stickers_text: [''],
      pr_custom_printing_text: [''],
      pr_bundling_text: [''],
      pr_various_prep_text: [''],
      pr_carton_arrangement_text: [''],
      pr_generic_quote_text: [''],

      od_sample_to_match: [''],
      od_sold_on_amazon: [''],
      od_pro_product_pictures: [''],
      od_need_help_in_prototype: [''],
      od_need_graphic_design: [''],
      od_special_certificates: [''],
      od_info_details: [''],

      destination_address: [''],
      destination_city: [''],
      destination_state: [''],
      destination_zip: [''],
      has_prototype: [''],
      has_packing_req: [''],
      other_details: [''],
      default_photo: ['0'],

      fileremarks: this.fb.array([]),
      shippingWeights: this.fb.array([]),
      productWeights: this.fb.array([]),
      dimensionalWeights: this.fb.array([]),
      boxWeights: this.fb.array([]),
      cartonWeights: this.fb.array([]),
      palletWeights: this.fb.array([]),

      material: this.fb.array([]),
      custom_fields: this.fb.array([]),
      product_links: this.fb.array([]),

      labor_cost: [0],
      package_cost: [0],
      default_shipping: [0],
      efficiency_score: [0],
      preferred_vendor_id: [0],

      internal_use_only: [false],
      photo: [''],
      product_design: [''],

      retail_price: [0],
      wholesale_price: [0],
      unitCost: [0],
      basePrice: [[]],

      mirror_inventory_from_parent: [false],

      product_tags: [[]],

      unit_class_id: [null, Validators.required],
      default_selling_unit_id: [null, Validators.required],
      default_purchase_unit_id: [null, Validators.required],

      default_receiving_bin_id: [null],

      kitItems: this.fb.array([]),
      type: ['PRODUCT'],

      lot_tracking: [false],
      customFieldValues: [''],
      tax_category: [''],
    });
  }

  private initActions() :void {
    this.unitClassService.getBaseUnit();
    this.rfqApiService.getCategories({});
    this.factoryApiService.get({});
    this.unitClassService.get({ limit: '999' });
    this.shippingCarrierApiService.get({});
    this.settingsApiService.getSetting();
    this.unitOfMeasureApiService.getAll({
      params: {
        unitClassId: this.form.getRawValue().unit_class_id,
        limit: '100',
      },
    });
  }

  private initSubscriptions() :void {
    this.getDefaultBaseUnit().subscribe();
    this.getCategories().subscribe();
    this.getRfq().subscribe();
  }

  private getDataFromService() :void {
    this.categories$ = this.rfqApiService.categories$;
    this.currentRfq$ = this.rfqApiService.currentRfq$;
    this.submittingForm$ = this.rfqApiService.submittingForm$;
    this.factories$ = this.factoryApiService.factories$;
    this.unitClasses$ = this.unitClassService.unitClasses$;
    this.baseUnitClass$ = this.unitClassService.baseUnitClass$;
    this.unitMeasures$ = this.unitOfMeasureApiService.unitOfMeasures$;
  }

  private getDefaultBaseUnit() :Observable<IUnitClass> {
    return this.baseUnitClass$.pipe(
      tap((baseUnitClass :IUnitClass) => {
        if (baseUnitClass) {
          this.form.patchValue({ unit_class_id: baseUnitClass?.id });
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getRfq() :Observable<any> {
    return this.currentRfq$.pipe(
      tap((baseUnitClass :IUnitClass) => {
        if (baseUnitClass) {
          this.form.patchValue({ unit_class_id: baseUnitClass?.id });
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getCategories() :Observable<ICategory[]> {
    return this.categories$.pipe(
      tap((categories :ICategory[]) => {
        if (categories) {
          this.categories = categories;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getFormattedData() :FormData {
    const prodType = DepartmentConfig.productTypes.find(
      (row) => row.id === this.form.get('product_type_id')?.value,
    );
    const prodDepartment = DepartmentConfig.productDepartments.find(
      (row) => row.id == this.form.get('product_dept_id')?.value,
    );
    const fd = new FormData();
    fd.append('business_account_id', '0');
    fd.append('added_by', '0');
    fd.append('product_dept', prodDepartment?.text || '');
    fd.append('product_dept_id', (prodDepartment?.id || 0).toString());
    fd.append('custom_fields', JSON.stringify(this.customFieldsControl.value));
    fd.append('product_link', JSON.stringify(this.productLinksControl.value));
    fd.append('materials', JSON.stringify(this.materialControl.value));
    fd.append('shipping_weight', JSON.stringify(this.shippingWeightsControl.value));
    fd.append(
      'dimensional_weight',
      JSON.stringify(this.dimensionalWeightsControl.value),
    );
    fd.append('labor_cost', this.form.get('labor_cost')?.value.toString());
    fd.append('package_cost', this.form.get('package_cost')?.value.toString());
    fd.append(
      'default_shipping',
      this.form.get('default_shipping')?.value.toString(),
    );
    fd.append(
      'efficiency_score',
      this.form.get('efficiency_score')?.value.toString(),
    );
    fd.append('product_name', this.form.get('product_name')?.value);
    fd.append(
      'product_type_id',
      this.form.get('product_type_id')?.value.toString(),
    );
    fd.append('product_type', prodType?.text || '');
    fd.append('internal_sku', this.form.get('internal_sku')?.value);
    fd.append('upc', this.form.get('upc')?.value);
    fd.append('product_description', this.form.get('product_description')?.value);
    fd.append('product_category_id', this.form.get('product_category_id')?.value);
    fd.append('units_qty_annual', this.form.get('units_qty_annual')?.value);
    fd.append('units_qty_first', this.form.get('units_qty_first')?.value);
    fd.append('target_price', this.form.get('target_price')?.value);
    fd.append(
      'will_purchase_sample',
      (this.form.get('will_purchase_sample')?.value ? 1 : 0).toString(),
    );
    fd.append('is_product_in_market', this.form.get('is_product_in_market')?.value);
    fd.append('destination_address', this.form.get('destination_address')?.value);
    fd.append('destination_city', this.form.get('destination_city')?.value);
    fd.append('destination_state', this.form.get('destination_state')?.value);
    fd.append('destination_zip', this.form.get('destination_zip')?.value);
    fd.append(
      'has_prototype',
      (this.form.get('has_prototype')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'has_packing_req',
      (this.form.get('has_packing_req')?.value ? 1 : 0).toString(),
    );
    fd.append('other_details', JSON.stringify(this.customFieldsControl.value));
    fd.append('default_photo', this.form.get('default_photo')?.value);
    fd.append(
      'expires_at',
      this.form.get('quote_expires') ? this.form.get('expires_at')?.value : '',
    );
    fd.append('reorder_level', this.form.get('reorder_level')?.value.toString());
    fd.append(
      'qty_limit_notify',
      this.form.get('qty_limit_notify')?.value.toString(),
    );
    fd.append(
      'oss_limit_notify',
      this.form.get('oss_limit_notify')?.value.toString(),
    );
    fd.append(
      'unit_class_id',
      (this.form.get('unit_class_id')?.value
        ? this.form.get('unit_class_id')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'default_selling_unit_id',
      (this.form.get('default_selling_unit_id')?.value
        ? this.form.get('default_selling_unit_id')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'default_purchase_unit_id',
      (this.form.get('default_purchase_unit_id')?.value
        ? this.form.get('default_purchase_unit_id')?.value
        : 0
      ).toString(),
    );

    fd.append(
      'is_palletized',
      (this.form.get('is_palletized')?.value
        ? this.form.get('is_palletized')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'cartons_per_pallet',
      (this.form.get('cartons_per_pallet')?.value
        ? this.form.get('cartons_per_pallet')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'shipment_value',
      (this.form.get('shipment_value')?.value
        ? this.form.get('shipment_value')?.value
        : 0
      ).toString(),
    );
    fd.append('product_weight', JSON.stringify(this.productWeightsControl.value));

    fd.append('box_weight', JSON.stringify(this.boxWeightsControl.value));
    fd.append(
      'box_length',
      (this.form.get('box_length')?.value
        ? this.form.get('box_length')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'box_width',
      (this.form.get('box_width')?.value
        ? this.form.get('box_width')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'box_height',
      (this.form.get('box_height')?.value
        ? this.form.get('box_height')?.value
        : 0
      ).toString(),
    );

    fd.append('carton_weight', JSON.stringify(this.cartonWeightsControl.value));
    fd.append(
      'carton_length',
      (this.form.get('carton_length')?.value
        ? this.form.get('carton_length')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'carton_width',
      (this.form.get('carton_width')?.value
        ? this.form.get('carton_width')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'carton_height',
      (this.form.get('carton_height')?.value
        ? this.form.get('carton_height')?.value
        : 0
      ).toString(),
    );

    fd.append('pallet_weight', JSON.stringify(this.palletWeightsControl.value));
    fd.append(
      'pallet_length',
      (this.form.get('pallet_length')?.value
        ? this.form.get('pallet_length')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'pallet_width',
      (this.form.get('pallet_width')?.value
        ? this.form.get('pallet_width')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'pallet_height',
      (this.form.get('pallet_height')?.value
        ? this.form.get('pallet_height')?.value
        : 0
      ).toString(),
    );

    fd.append(
      'shipping_length',
      (this.form.get('shipping_length')?.value
        ? this.form.get('shipping_length')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'shipping_width',
      (this.form.get('shipping_width')?.value
        ? this.form.get('shipping_width')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'shipping_height',
      (this.form.get('shipping_height')?.value
        ? this.form.get('shipping_height')?.value
        : 0
      ).toString(),
    );

    fd.append(
      'product_length',
      (this.form.get('product_length')?.value
        ? this.form.get('product_length')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'product_width',
      (this.form.get('product_width')?.value
        ? this.form.get('product_width')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'product_height',
      (this.form.get('product_height')?.value
        ? this.form.get('product_height')?.value
        : 0
      ).toString(),
    );

    fd.append(
      'units_per_carton',
      this.form.get('units_per_carton')?.value
        ? this.form.get('units_per_carton')?.value
        : '',
    );
    fd.append(
      'units_per_pallet',
      this.form.get('units_per_pallet')?.value
        ? this.form.get('units_per_pallet')?.value
        : '',
    );

    fd.append('rfq_quote_type', this.form.get('rfq_quote_type')?.value);
    fd.append('volume_first_order', this.form.get('volume_first_order')?.value);
    fd.append('volume_annual_order', this.form.get('volume_annual_order')?.value);

    fd.append(
      'pr_labeling_stickers',
      (this.form.get('pr_labeling_stickers')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'pr_custom_printing',
      (this.form.get('pr_custom_printing')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'pr_bundling',
      (this.form.get('pr_bundling')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'pr_various_prep',
      (this.form.get('pr_various_prep')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'pr_carton_arrangement',
      (this.form.get('pr_carton_arrangement')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'pr_same_as_link',
      (this.form.get('pr_same_as_link')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'pr_generic_quote',
      (this.form.get('pr_generic_quote')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'pr_custom_printing_text',
      this.form.get('pr_custom_printing_text')?.value
        ? this.form.get('pr_custom_printing_text')?.value
        : '',
    );
    fd.append(
      'pr_bundling_text',
      this.form.get('pr_bundling_text')?.value
        ? this.form.get('pr_bundling_text')?.value
        : '',
    );
    fd.append(
      'pr_various_prep_text',
      this.form.get('pr_various_prep_text')?.value
        ? this.form.get('pr_various_prep_text')?.value
        : '',
    );
    fd.append(
      'pr_carton_arrangement_text',
      this.form.get('pr_carton_arrangement_text')?.value
        ? this.form.get('pr_carton_arrangement_text')?.value
        : '',
    );
    fd.append(
      'pr_generic_quote_text',
      this.form.get('pr_generic_quote_text')?.value
        ? this.form.get('pr_generic_quote_text')?.value
        : '',
    );
    fd.append(
      'od_sample_to_match',
      (this.form.get('od_sample_to_match')?.value
        ? this.form.get('od_sample_to_match')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'od_sold_on_amazon',
      (this.form.get('od_sold_on_amazon')?.value
        ? this.form.get('od_sold_on_amazon')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'od_pro_product_pictures',
      (this.form.get('od_pro_product_pictures')?.value
        ? this.form.get('od_pro_product_pictures')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'od_need_help_in_prototype',
      (this.form.get('od_need_help_in_prototype')?.value
        ? this.form.get('od_need_help_in_prototype')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'od_need_graphic_design',
      (this.form.get('od_need_graphic_design')?.value
        ? this.form.get('od_need_graphic_design')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'od_special_certificates',
      (this.form.get('od_special_certificates')?.value
        ? this.form.get('od_special_certificates')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'od_info_details',
      this.form.get('od_info_details')?.value
        ? this.form.get('od_info_details')?.value
        : '',
    );
    fd.append(
      'units_qty_annual_type',
      (this.form.get('units_qty_annual_type')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'address_not_known',
      (this.form.get('address_not_known')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'changes_to_make',
      this.form.get('changes_to_make')?.value
        ? this.form.get('changes_to_make')?.value
        : '',
    );
    fd.append(
      'development_status',
      this.form.get('development_status')?.value
        ? this.form.get('development_status')?.value
        : '',
    );
    fd.append(
      'units_qty_moq',
      (this.form.get('units_qty_moq')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'brand_product',
      (this.form.get('brand_product')?.value
        ? this.form.get('brand_product')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'artwork_available',
      (this.form.get('artwork_available')?.value
        ? this.form.get('artwork_available')?.value
        : 0
      ).toString(),
    );
    fd.append(
      'product_has_variants',
      (this.form.get('product_has_variants')?.value ? 1 : 0).toString(),
    );

    fd.append('product_variants', JSON.stringify([])); // finalyze values upon testing
    fd.append(
      'preferred_vendor_id',
      this.form.get('preferred_vendor_id')?.value.toString(),
    );
    fd.append('retail_price', this.form.get('retail_price')?.value);
    fd.append('wholesale_price', this.form.get('wholesale_price')?.value);
    fd.append('unitCost', this.form.get('unitCost')?.value);
    fd.append('basePrice', JSON.stringify(this.form.get('basePrice')?.value));

    fd.append('product_tags', this.form.get('product_tags')?.value);
    fd.append('tax_category', this.form.get('tax_category')?.value || '');

    fd.append(
      'internal_use_only',
      (this.form.get('internal_use_only')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'mirror_inventory_from_parent',
      (this.form.get('mirror_inventory_from_parent')?.value ? 1 : 0).toString(),
    );
    fd.append(
      'default_receiving_bin_id',
      this.form.get('default_receiving_bin_id')?.value,
    );

    if (this.data.parentSku && this.data.parentSku.id > 0) {
      fd.append('variant_parent_id', this.data.parentSku.id.toString());
    }

    if (this.form.get('product_category_id')?.value) {
      const category :ICategory | undefined = this.categories.find(
        (row) => row.id === this.form.get('product_category_id')?.value,
      );
      fd.append('product_category', category?.product_category || '');

      if (
        this.form.get('sub_category_id')?.value &&
        this.form.get('sub_category_id')?.value > 0
      ) {
        fd.append(
          'sub_category_id',
          this.form.get('sub_category_id')?.value.toString(),
        );
        const subCategory :any = category?.sub_categories.find(
          (row :any) => row.id == this.form.get('sub_category_id')?.value,
        );
        if (subCategory) {
          fd.append('sub_category', subCategory.sub_category);
        }
      }
    }

    fd.append('lot_tracking', this.form.get('lot_tracking')?.value ? '1' : '0');

    if (this.form.get('type')?.value) {
      fd.append('type', this.form.get('type')?.value);
    }

    if (this.form.get('type')?.value === EProductTypes.KIT) {
      this.kitItemsControl.value.forEach((item :any) => {
        delete item.sku;
      });

      fd.append('kitItems', JSON.stringify(this.kitItemsControl.value));
    }

    if (this.form.get('photo')?.value) {
      fd.append('photo', this.form.get('photo')?.value);
    }
    if (this.form.get('product_design')?.value) {
      fd.append('product_design', this.form.get('product_design')?.value);
    }

    const rfq :any = this.data.rfq;

    if (rfq != undefined && rfq.variant_parent_id != undefined) {
      if (rfq.variant_parent_id > 0 && rfq.parent) {
        fd.append('product_category', rfq.parent.product_category);
        fd.append('product_category_id', rfq.parent.product_category_id);
        fd.append('sub_category', rfq.parent.sub_category);
        fd.append('sub_category_id', rfq.parent.sub_category_id);
        fd.append('internal_use_only', rfq.parent.internal_use_only);
        fd.append('default_shipping', rfq.parent.default_shipping);
      }
    }

    fd.append(
      'customFieldValues',
      this.form.get('customFieldValues')?.value || '[]',
    );

    return fd;
  }

  private uploadFiles(rfq :any) :void {
    this.fileRemarksControl.controls.forEach(
      (fileremark :AbstractControl, idx :number) => {
        const control = fileremark as UntypedFormControl;
        const fd = new FormData();

        if (control.value.file) {
          fd.append('file', control.value.file);
          fd.append('remarks', control.value.remarks);

          this.rfqApiService
            .uploadAttachments({ id: rfq.id, payload: fd })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: () => {
                this.uploadedFilesCount += 1;

                if (!this.isUploading && this.uploadErrors.length === 0) {
                  this.snackBar.open(
                    'Successfully created client SKU.',
                    'Success',
                    {
                      duration: SnackBarConfig.DEFAULT.duration,
                    },
                  );

                  this.onClose();
                }
              },
              error: (err) =>
                throwError(() => {
                  this.uploadErrors.push(control.value.file);
                  this.snackBar.open(
                    'Some files were not uploaded. Please upload them again manually.',
                    'File Upload Error',
                    {
                      duration: SnackBarConfig.DEFAULT.duration,
                    },
                  );
                }),
            });
        }
      },
    );
  }

  get mode() :string {
    return this.data?.rfq ? 'Update' : 'Create';
  }

  get typeControl() :UntypedFormArray {
    return this.form.get('type') as UntypedFormArray;
  }

  get kitItemsControl() :UntypedFormArray {
    return this.form.get('kitItems') as UntypedFormArray;
  }

  get customFieldsControl() :UntypedFormArray {
    return this.form.get('custom_fields') as UntypedFormArray;
  }

  get productLinksControl() :UntypedFormArray {
    return this.form.get('product_links') as UntypedFormArray;
  }

  get materialControl() :UntypedFormArray {
    return this.form.get('material') as UntypedFormArray;
  }

  get productWeightsControl() :UntypedFormArray {
    return this.form.get('productWeights') as UntypedFormArray;
  }

  get shippingWeightsControl() :UntypedFormArray {
    return this.form.get('shippingWeights') as UntypedFormArray;
  }

  get dimensionalWeightsControl() :UntypedFormArray {
    return this.form.get('dimensionalWeights') as UntypedFormArray;
  }

  get boxWeightsControl() :UntypedFormArray {
    return this.form.get('boxWeights') as UntypedFormArray;
  }

  get cartonWeightsControl() :UntypedFormArray {
    return this.form.get('cartonWeights') as UntypedFormArray;
  }

  get palletWeightsControl() :UntypedFormArray {
    return this.form.get('palletWeights') as UntypedFormArray;
  }

  get fileRemarksControl() :UntypedFormArray {
    return this.form.get('fileremarks') as UntypedFormArray;
  }

  get isKit() :boolean {
    return this.form.get('type')?.value === EProductTypes.KIT;
  }

  get newKitsStructureEnabled() :boolean {
    return this.data?.featureFlag?.newKitsStructureEnabled && this.isKit;
  }

  get hasKitItems() :boolean {
    return (
      this.form.get('kitItems')?.value.filter((item :any) => item.productId)
        .length > 0
    );
  }

  get isUploading() :boolean {
    if (this.fileRemarksControl.getRawValue()?.length > 0 && this.uploadInitiated) {
      return (
        this.uploadedFilesCount < this.fileRemarksControl.getRawValue()?.length
      );
    }

    return false;
  }

  get customFieldsEnabled() :boolean {
    return this.data?.featureFlag?.customFieldsEnabled;
  }
}
